import { useTemplateContext } from "../provider";
import { VerificationSchemaView } from "../slices/types";
import { tabTitle } from "../utils";
import { Show } from "shared/components";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { ApplicantType, VerificationNames } from "shared/types";
import { useEffect } from "react";
import {
  basicInformationTabs,
  combinedIncomeTabs,
  criminalReportTabs,
  enhancedCriminalReportTabs,
  identityVerificationTabs,
  incomeHistoryTabs,
  countWalkInTabs,
  screeningServicesTabs,
  petScreeningReportTabs,
  VERIFICATIONS_WITH_TABS,
  housingHistoryTabs,
  rentalVerificationTabs,
  boomFinanceTabs
} from "../constant";
import { cx } from "../../../../utils/classnames";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { SwitchWithReadOnlyMode } from "./SwitchWithReadOnlyMode";
import { getApplicantRole } from "utils/getApplicantRole";
import { HousingHistoryTierPill } from "shared/app/HousingHistoryTierPill";
import { Template } from "types/landlord/verificationTemplate";
import { withRoleAllowedForEnabledInviters } from "../slices/ApplicationGroupSlice/helpers";
import { countAdditionalQuestions } from "../additionalQuestions";

const HEADER_CONTENT = {
  [ApplicantType.Applicant]: {
    title: "Applicant application",
    description:
      "Applicant is an individual over 18 living in the property, like a roommate, spouse, or partner."
  },
  [ApplicantType.CoSigner]: {
    title: "Guarantor application",
    description:
      "Guarantor is someone who won’t live in the property but will provide financial support."
  },
  [ApplicantType.Occupant]: {
    title: "Occupant application",
    description:
      "Occupant is someone who lives in the property but isn’t financially responsible for the lease."
  },
  [VerificationSchemaView.TenantQualifications]: {
    title: tabTitle[VerificationSchemaView.TenantQualifications],
    description:
      "Specify any resident qualifications or information for applicants and guarantors to see on the first page."
  },
  [VerificationSchemaView.PetPolicy]: {
    title: tabTitle[VerificationSchemaView.PetPolicy],
    description: (
      <>
        This template is the source of truth for both PMS-synced and manually
        added properties.
        <br />
        When pet policy is on, applicants see exactly what you set here.
      </>
    )
  },
  [VerificationSchemaView.UnderwritingAgent]: {
    title: tabTitle[VerificationSchemaView.UnderwritingAgent],
    description: "Reviews applications for you"
  },
  [VerificationSchemaView.ApplicationGroup]: {
    title: tabTitle[VerificationSchemaView.ApplicationGroup],
    description:
      "Configure the applicant’s ability to invite additional parties to their application."
  },
  [VerificationSchemaView.SubmissionDisclosure]: {
    title: tabTitle[VerificationSchemaView.SubmissionDisclosure],
    description:
      "Is there specific information for the applicant or guarantor? It will appear before submitting the application."
  },
  [VerificationSchemaView.CountyWalkIn]: {
    title: "County criminal record search"
  },
  [VerificationSchemaView.PremiumWarningsReport]: {
    title: tabTitle[VerificationSchemaView.PremiumWarningsReport],
    description:
      "Choose what happens when each check finds something. A check set to Fail pauses credit, criminal and eviction reports until you override."
  },
  [VerificationSchemaView.AdverseAction]: {
    title: "Select adverse action template",
    description:
      "Adverse action notice will be sent to applicants when an application is declined or conditionally approved based on obtained information"
  },
  [VerificationSchemaView.DiscountCodes]: {
    title: tabTitle[VerificationSchemaView.DiscountCodes],
    description:
      "Share discount codes with applicants so they can apply them during checkout to reduce their application fee"
  },
  [VerificationSchemaView.AdverseActionGenericTemplate]: {
    title: "Generic template"
  },
  [VerificationSchemaView.AdverseActionWATemplate]: {
    title: "WA template"
  }
};

const HEADER_CRUMBS = {
  [VerificationSchemaView.AdverseActionGenericTemplate]: [
    {
      title: "Adverse action",
      verificationSchemaView: VerificationSchemaView.AdverseAction
    },
    {
      title:
        HEADER_CONTENT[VerificationSchemaView.AdverseActionGenericTemplate]
          .title,
      verificationSchemaView:
        VerificationSchemaView.AdverseActionGenericTemplate
    }
  ],
  [VerificationSchemaView.AdverseActionWATemplate]: [
    {
      title: "Adverse action",
      verificationSchemaView: VerificationSchemaView.AdverseAction
    },
    {
      title:
        HEADER_CONTENT[VerificationSchemaView.AdverseActionWATemplate].title,
      verificationSchemaView: VerificationSchemaView.AdverseActionWATemplate
    }
  ]
};

const getHeaderCrumbs = (
  setSelectedVerificationSchema: (
    selectedVerification: VerificationSchemaView
  ) => void,
  selectedVerificationSchema: VerificationSchemaView,
  partnerType: ApplicantType
) => {
  return (
    HEADER_CRUMBS[selectedVerificationSchema]?.map((crumb) => ({
      title: crumb.title,
      onClick: () =>
        setSelectedVerificationSchema(crumb.verificationSchemaView),
      isActive: selectedVerificationSchema === crumb.verificationSchemaView
    })) || [
      {
        title: `${getApplicantRole(partnerType)} application`,
        onClick: () =>
          setSelectedVerificationSchema(
            VerificationSchemaView.ScreeningServices
          )
      },
      {
        title:
          getHeader(selectedVerificationSchema, partnerType)?.title ||
          tabTitle[selectedVerificationSchema],
        isActive: true,
        onClick: () => setSelectedVerificationSchema(selectedVerificationSchema)
      }
    ]
  );
};

const getHeader = (
  verificationType: VerificationSchemaView,
  applicantType: ApplicantType
) => {
  if (verificationType === VerificationSchemaView.ScreeningServices)
    return HEADER_CONTENT[applicantType];

  return (
    HEADER_CONTENT[verificationType] || {
      title:
        VERIFICATIONS_WITH_TABS.find(({ tabs }) =>
          tabs.includes(verificationType)
        )?.title || tabTitle[verificationType],
      description: ""
    }
  );
};

export const LayoutHeader = () => {
  const {
    partnerType,
    selectedVerificationSchema,
    setSelectedVerificationSchema,
    setSelectedStep,
    isModal,
    template,
    savedTemplate,
    setTemplate,
    readOnly
  } = useTemplateContext();
  const { title, description } = getHeader(
    selectedVerificationSchema,
    partnerType
  );

  const headerCrumbs = getHeaderCrumbs(
    setSelectedVerificationSchema,
    selectedVerificationSchema,
    partnerType
  );

  const questionCounts: Partial<Record<VerificationSchemaView, number>> = {
    [VerificationSchemaView.HousingHistoryAdditionalQuestions]:
      countAdditionalQuestions({
        template,
        partnerType,
        verificationName: VerificationNames.HousingHistory
      }),
    [VerificationSchemaView.CombinedAdditionalQuestions]:
      countAdditionalQuestions({
        template,
        partnerType,
        verificationName: VerificationNames.CombinedIncome
      })
  };

  const getTabs = (
    tabs: VerificationSchemaView[],
    defaultLabel: VerificationSchemaView | null
  ): Tab<VerificationSchemaView>[] =>
    tabs.map((schema) => {
      const count = questionCounts[schema];
      return {
        value: schema,
        label: schema === defaultLabel ? "General" : tabTitle[schema],
        rightElement:
          count === undefined ? undefined : { type: "counter", count }
      };
    });

  const tabSections = [
    { tabs: basicInformationTabs, label: null, setActiveTab: setSelectedStep },
    {
      tabs: identityVerificationTabs,
      label: VerificationSchemaView.IdentityVerification
    },
    {
      tabs: petScreeningReportTabs,
      label: VerificationSchemaView.PetScreeningReport
    },
    {
      tabs: boomFinanceTabs,
      label: VerificationSchemaView.BoomFinanceVerification
    },
    {
      tabs: incomeHistoryTabs,
      label: VerificationSchemaView.IncomeHistory,
      setActiveTab: setSelectedStep,
      startedTab: 3
    },
    {
      tabs: combinedIncomeTabs,
      label: VerificationSchemaView.CombinedIncomeVerification,
      setActiveTab: setSelectedStep,
      startedTab: 0
    },
    {
      tabs: housingHistoryTabs,
      label: VerificationSchemaView.HousingHistoryGeneralConfiguration
    },
    {
      tabs: rentalVerificationTabs,
      label: VerificationSchemaView.RentalVerification
    },
    { tabs: criminalReportTabs, label: VerificationSchemaView.CriminalReport },
    {
      tabs: enhancedCriminalReportTabs,
      label: VerificationSchemaView.EnhancedCriminalGeneral
    },
    {
      tabs: countWalkInTabs,
      label: VerificationSchemaView.CountyWalkIn
    }
  ];

  useEffect(() => {
    // Reset step when switching to Screening Services
    if (
      selectedVerificationSchema === VerificationSchemaView.ScreeningServices ||
      selectedVerificationSchema === VerificationSchemaView.CountyWalkIn
    )
      setSelectedStep(0);
  }, [selectedVerificationSchema]);

  const togglePartnerType = () => {
    if (!template || !savedTemplate) return;

    const enabled = !template.settings[partnerType]?.enabled_screening_services;
    const updatedTemplate: Template = {
      ...template,
      settings: {
        ...template.settings,
        [partnerType]: {
          ...template.settings[partnerType],
          enabled_screening_services: enabled
        }
      },
      verification_schemas: {
        ...template.verification_schemas,
        [partnerType]: {
          ...savedTemplate.verification_schemas.applicant
        }
      }
    };

    setTemplate(
      enabled
        ? withRoleAllowedForEnabledInviters(updatedTemplate, partnerType)
        : updatedTemplate
    );
  };

  const togglePetPolicyEnabled = () => {
    if (!template) return;

    setTemplate({
      ...template,
      pet_policy: {
        ...template.pet_policy,
        enabled: !template.pet_policy?.enabled
      }
    });
  };

  const shouldShowBreadcrumbs =
    [
      VerificationSchemaView.CreditReport,
      VerificationSchemaView.AdverseActionGenericTemplate,
      VerificationSchemaView.AdverseActionWATemplate
    ].includes(selectedVerificationSchema) ||
    screeningServicesTabs.includes(selectedVerificationSchema);

  return (
    <div
      className={cx(
        screeningServicesTabs.includes(selectedVerificationSchema)
          ? ""
          : "border-b border-alpha-100",
        "w-full"
      )}
    >
      <Show if={shouldShowBreadcrumbs}>
        <div className="px-6 py-[22px] border-b border-alpha-40 bg-background-1000">
          <BreadCrumbs crumbs={headerCrumbs} />
        </div>
      </Show>

      <div className="bg-background-1000 md:px-8 px-4">
        <div className="md:py-6 py-3">
          <div
            className={cx(
              "flex justify-between",
              selectedVerificationSchema === VerificationSchemaView.PetPolicy
                ? "items-start"
                : "items-center"
            )}
          >
            <div className="space-y-2">
              {selectedVerificationSchema ===
              VerificationSchemaView.PremiumWarningsReport ? (
                <h3 className="font-bold text-base md:text-xl flex items-center gap-3">
                  {title}
                  <HousingHistoryTierPill isPremium />
                </h3>
              ) : (
                <h3 className="font-bold text-base md:text-xl">{title}</h3>
              )}
              {description ? (
                <p className="text-content-400 text-xs md:text-base">
                  {description}
                </p>
              ) : null}
            </div>

            <Show
              if={
                selectedVerificationSchema ===
                  VerificationSchemaView.ScreeningServices &&
                [ApplicantType.CoSigner, ApplicantType.Occupant].includes(
                  partnerType
                )
              }
            >
              <SwitchWithReadOnlyMode
                label={
                  partnerType === ApplicantType.CoSigner
                    ? "Allow guarantors"
                    : "Allow occupants"
                }
                checked={Boolean(
                  template?.settings[partnerType]?.enabled_screening_services
                )}
                onChange={() => togglePartnerType()}
                readOnly={readOnly}
              />
            </Show>
            <Show
              if={
                selectedVerificationSchema === VerificationSchemaView.PetPolicy
              }
            >
              <SwitchWithReadOnlyMode
                label="Set up pet restrictions"
                checked={Boolean(template?.pet_policy?.enabled)}
                onChange={() => togglePetPolicyEnabled()}
                readOnly={readOnly}
              />
            </Show>
          </div>
        </div>

        <div
          className={cx(
            isModal ? "max-w-[calc(90vw-312px)]" : "max-w-[calc(100vw-370px)]"
          )}
        >
          {tabSections.map(
            ({
              tabs,
              label,
              setActiveTab = setSelectedVerificationSchema,
              startedTab
            }) => (
              <Show
                key={tabs.join(",")}
                if={tabs.includes(selectedVerificationSchema)}
              >
                <Tabs
                  tabStyle="line"
                  size="sm"
                  fullWidthTabs={false}
                  className="overflow-x-auto hide-scroll"
                  tabs={getTabs(tabs, label)}
                  active={selectedVerificationSchema}
                  onChange={(t) => {
                    if (
                      setActiveTab === setSelectedStep &&
                      t !== VerificationSchemaView.AdditionalQuestions
                    )
                      setSelectedStep(
                        tabs.findIndex((schema) => schema === t) -
                          (startedTab ?? 0)
                      );

                    setSelectedVerificationSchema(t as VerificationSchemaView);
                  }}
                />
              </Show>
            )
          )}
        </div>
      </div>
    </div>
  );
};
